import _ from 'lodash';
import { clsx } from 'clsx';
import { Fragment } from 'react';
import { Box, Checkbox } from '@material-ui/core';

import { ProductComment } from './components/product-comment';
import { SuggestedProductComment } from './components/suggested-product-comment';
import Typography from '../common/typography';
import CustomCheckbox from '../common/custom-checkbox/CustomCheckbox';
import { TrashIcon } from '../icons/trash-icon';
import {
  ChipOrderDisclaimer,
  OrderMgmtDisclaimer,
} from '../order-management/components/order-mgmt-dialog/components';

import { useFormatMessage } from '../../intl/intl';
import { formatCurrency } from '../../utils/CurrencyUtils';
import { useOrderManagement } from '../order-management/hooks';

import StringUtils from '../../utils/StringUtils';
import { orderDetailCardStyles } from './OrderDetailCard.styles';
import { FORMAT_SPACES } from './const';
import { CheckBoxOutlineBlank, CheckBoxOutlined } from '@material-ui/icons';

export function OrderDetailCard(props: Props) {
  const {
    classes,
    product,
    showAddWhimsButton,
    deactivateProductIds,
    deactivateToppingIds,
    handleCheckDefectProduct,
    isDefectProductsChecked,
  } = props;

  const {
    isSendingUserApproval,
    hasNoUserResponse,
    handleCheckProductSelected,
    handleSelectTopping,
    checkIfProductIsSelected,
    checkIfToppingIsSelected,
    checkIfProductHasToppingsSelected,
    hasOrderOnlyOneProduct,
    handleRemoveProduct,
    handleCheckIfProductHasBeenRemoved,
    hasOrderProductsRemoved,
    hasOrderProductsSelected,
  } = useOrderManagement();

  const {
    comments,
    description,
    orderedToppings,
    id,
    name,
    quantityText,
    toppingMessage,
    total_real_price,
    replacement_suggestion,
    has_defect_rate_report,
    global_offers,
    isDefectChecked,
    order_product_id,
  } = product;

  const isProductDeactivated = deactivateProductIds.includes(id);
  const isProductSelected = checkIfProductIsSelected(id);
  const _classes = orderDetailCardStyles();
  const globalOfferTagPromo =
    global_offers?.[0]?.conditions?.[0]?.tag_promo ?? '';
  const defectRateTitle = useFormatMessage({ id: 'defectRate.title' });
  const defectRateCaption = useFormatMessage({ id: 'defectRate.caption' });
  const commentsText = useFormatMessage({
    id: 'orderStatusCookingDetail.comments',
  });
  const addValue = useFormatMessage({
    id: 'orderStatusCookingDetail.addValue',
  });

  const globalOfferText = useFormatMessage(
    { id: 'globalOffer.text' },
    {
      promo: globalOfferTagPromo,
    },
  );
  const globalOfferMsgError = useFormatMessage({ id: 'globalOffer.msg.error' });

  const globalOfferPromotion = useFormatMessage({
    id: 'globalOffer.promotion',
  });

  const disclaimerDescriptionToProduct = useFormatMessage({
    id: 'orderManagement.deactivateItems.disclaimerDescription.toProduct',
  });
  const disclaimerDescriptionToTopping = useFormatMessage({
    id: 'orderManagement.deactivateItems.disclaimerDescription.toTopping',
  });
  const deactivateItemsDescriptionText = ({ item, itemName }) => {
    const _text =
      item === 'product'
        ? disclaimerDescriptionToProduct
        : disclaimerDescriptionToTopping;
    return _text.replace('_itemName_', itemName);
  };
  const deactivateItemsTitleText = useFormatMessage({
    id: 'orderManagement.deactivateItems.disclaimerTitle',
  });
  const soldOutProductText = useFormatMessage({
    id: 'orderManagement.deactivateItems.soldOutProduct',
  });
  const soldOutToppingText = useFormatMessage({
    id: 'orderManagement.deactivateItems.soldOutTopping',
  });

  const isReplacementSuggestionValid = !StringUtils.isNullOrEmpty(
    replacement_suggestion,
  );
  const isNullGlobalOffer =
    global_offers === null ||
    global_offers?.[0]?.conditions === null ||
    global_offers?.[0]?.conditions?.[0]?.tag_promo === null;
  const hasGlobalOffer = !StringUtils.isNullOrEmpty(globalOfferTagPromo);
  const showDetail = !_.isEmpty(orderedToppings) || comments || description;

  return (
    !handleCheckIfProductHasBeenRemoved(id) && (
      <div className={clsx(_classes.root, classes?.root)}>
        <div className={clsx(_classes.wrapper, classes?.wrapper)}>
          {isReplacementSuggestionValid && (
            <SuggestedProductComment
              productNameToBeReplaced={name}
              suggestedProduct={replacement_suggestion}
            />
          )}
          <div className={_classes.container}>
            {/* Header */}
            <Box className={_classes.detailHeader}>
              <Typography classes={{ root: _classes.quantity }}>
                {quantityText}
              </Typography>
              <Box className={_classes.titleWrapper}>
                <Box display='flex' alignItems='center' gridGap='0.5rem'>
                  <Typography classes={{ root: _classes.productTitle }}>
                    {name}
                  </Typography>
                  {isProductDeactivated && (
                    <ChipOrderDisclaimer
                      type='alert'
                      disclaimer={soldOutProductText}
                      classes={{
                        disclaimer: _classes.chipDisclaimer,
                        text: _classes.chipText,
                      }}
                    />
                  )}
                  {has_defect_rate_report && (
                    <ChipOrderDisclaimer
                      type='danger'
                      disclaimer={defectRateTitle}
                      classes={{
                        disclaimer: _classes.chipDisclaimer,
                        text: _classes.chipText,
                        icon: _classes.chipDangerIcon,
                      }}
                      hasIcon
                    />
                  )}
                  {(hasGlobalOffer || isNullGlobalOffer) && (
                    <ChipOrderDisclaimer
                      type='info'
                      disclaimer={globalOfferPromotion}
                      classes={{
                        disclaimer: _classes.chipDisclaimer,
                        text: _classes.chipText,
                      }}
                    />
                  )}
                </Box>
                {/* subtotal */}
                <div className={_classes.detailSubtotal}>
                  <Typography classes={{ root: _classes.subtotalVal }}>
                    <span className={_classes.subtotalValLabel}>Subtotal</span>
                    &nbsp;{formatCurrency(total_real_price)}
                  </Typography>
                  {(isSendingUserApproval || hasNoUserResponse) && (
                    <Box display='flex' alignItems='center'>
                      <CustomCheckbox
                        classes={{ root: _classes.checkProduct }}
                        checked={isProductSelected}
                        onChange={() => handleCheckProductSelected(id)}
                        value={isProductSelected}
                        disabled={hasOrderProductsRemoved}
                      />
                      {isSendingUserApproval && (
                        <Box
                          className={clsx(_classes.deleteProduct, {
                            [_classes.deleteProductsDisabled]:
                              isProductSelected ||
                              hasOrderOnlyOneProduct ||
                              hasOrderProductsSelected,
                          })}
                          onClick={() =>
                            isProductSelected ||
                            hasOrderOnlyOneProduct ||
                            hasOrderProductsSelected
                              ? null
                              : handleRemoveProduct(id)
                          }
                        >
                          <TrashIcon
                            type={
                              isProductSelected ||
                              hasOrderOnlyOneProduct ||
                              hasOrderProductsSelected
                                ? 'disabled'
                                : 'default'
                            }
                          />
                        </Box>
                      )}
                    </Box>
                  )}
                </div>
              </Box>
            </Box>
            <Box>
              {isProductDeactivated && (
                <OrderMgmtDisclaimer
                  description={deactivateItemsDescriptionText({
                    item: 'product',
                    itemName: name,
                  })}
                />
              )}
              {has_defect_rate_report && (
                <Box
                  className={clsx(
                    _classes.defectRate,
                    _classes?.defectRateBorder,
                  )}
                >
                  <Typography classes={{ root: _classes.defectRateCaption }}>
                    {defectRateCaption}
                  </Typography>
                  <Checkbox
                    disabled={isDefectProductsChecked}
                    checked={isDefectChecked}
                    onChange={() => handleCheckDefectProduct(order_product_id)}
                    value={isDefectChecked}
                    icon={<CheckBoxOutlineBlank />}
                    checkedIcon={<CheckBoxOutlined />}
                    classes={{
                      root: _classes.defectCheckedRoot,
                      checked: _classes.defectChecked,
                    }}
                  />
                </Box>
              )}
              {hasGlobalOffer && (
                <Box
                  className={clsx(
                    _classes.globalOffer,
                    _classes?.globalOfferBorder,
                  )}
                >
                  <Box
                    className={_classes.globalOfferCaption}
                    dangerouslySetInnerHTML={{
                      __html: globalOfferText || '',
                    }}
                  />
                </Box>
              )}
              {isNullGlobalOffer && (
                <div
                  className={clsx(
                    _classes.globalOffer,
                    _classes?.globalOfferBorder,
                  )}
                >
                  <span className={_classes.globalOfferCaption}>
                    {globalOfferMsgError}
                  </span>
                </div>
              )}
            </Box>
            {/* order body */}
            {showDetail && (
              <div className={_classes.detailContainer}>
                <p
                  className={_classes.description}
                  hidden={description.trim().length <= 3}
                >
                  {description.trim()}
                </p>
                {/* Topping custom alert */}
                {toppingMessage && (
                  <div className={_classes.toppingDisclaimer}>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: toppingMessage,
                      }}
                    ></span>
                  </div>
                )}
                {orderedToppings && (
                  <div className={_classes.toppings}>
                    {orderedToppings.map((topping: any, index: number) => {
                      const isToppingDeactivated = deactivateToppingIds.includes(
                        topping.id,
                      );
                      return (
                        <Fragment key={index}>
                          <p
                            className={_classes.toppingCategory}
                            hidden={
                              topping.topping_category ===
                              orderedToppings[index - 1]?.topping_category
                            }
                          >
                            {`${'\xa0'.repeat(
                              (topping?.level ?? 0) * FORMAT_SPACES.category,
                            )}${topping.topping_category}`}
                          </p>
                          <Box
                            key={topping.id}
                            className={_classes.toppingDetail}
                            display='flex'
                            flexDirection='column'
                          >
                            <Box
                              display='flex'
                              width='100%'
                              alignItems='center'
                              justifyContent='space-between'
                            >
                              <Box
                                display='flex'
                                alignItems='center'
                                gridGap='0.5rem'
                              >
                                <Typography
                                  classes={{
                                    root: _classes.toppingDescription,
                                  }}
                                >
                                  {`${'\xa0'.repeat(
                                    (topping?.level ?? 0) *
                                      FORMAT_SPACES.description,
                                  )}`}
                                  {topping.units > 1 && (
                                    <>{topping.units}x&ensp;</>
                                  )}
                                  {topping.description}
                                </Typography>
                                {isToppingDeactivated && (
                                  <ChipOrderDisclaimer
                                    type='warning'
                                    disclaimer={soldOutToppingText}
                                    classes={{
                                      disclaimer: _classes.chipDisclaimer,
                                      text: _classes.chipText,
                                    }}
                                  />
                                )}
                              </Box>
                              <Box className={_classes.toppingOptions}>
                                {(isSendingUserApproval ||
                                  hasNoUserResponse) && (
                                  <CustomCheckbox
                                    classes={{
                                      root: _classes.checkTopping,
                                    }}
                                    checked={checkIfToppingIsSelected(
                                      id,
                                      topping.id,
                                    )}
                                    onChange={() =>
                                      handleSelectTopping(id, topping.id)
                                    }
                                    value={checkIfToppingIsSelected(
                                      id,
                                      topping.id,
                                    )}
                                    disabled={
                                      isProductSelected ||
                                      checkIfProductHasToppingsSelected(
                                        id,
                                        topping.id,
                                      )
                                    }
                                  />
                                )}
                              </Box>
                              <p className={_classes.toppingPrice}>
                                {formatCurrency(topping.total_real_price)}
                              </p>
                            </Box>
                            {isToppingDeactivated && (
                              <OrderMgmtDisclaimer
                                description={deactivateItemsDescriptionText({
                                  item: 'topping',
                                  itemName: topping.description,
                                })}
                              />
                            )}
                          </Box>
                        </Fragment>
                      );
                    })}
                  </div>
                )}
                {!isReplacementSuggestionValid && comments && (
                  <ProductComment
                    textLabel={commentsText}
                    comments={comments}
                    showButton={showAddWhimsButton}
                    buttonLabel={addValue}
                    productName={name}
                    productId={id}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  );
}

interface Props {
  classes: any;
  intl: { formatMessage: any };
  product: any;
  showAddWhimsButton: boolean;
  deactivateProductIds: Array<number>;
  deactivateToppingIds: Array<number>;
  handleCheckDefectProduct: (id: number) => {};
  isDefectProductsChecked: boolean;
}
